import React, { useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  getPaginationRowModel, // Re-added for traditional pagination
} from '@tanstack/react-table';
import {
  ChevronUp,
  Loader2,
  ChevronDown,
  ChevronsUpDown,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle, // Added AlertCircle import
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

interface TableHeaderProps {
  title: string;
  description?: string; // New prop for description
  itemCount: number;
  onSearch: (query: string) => void;
  buttons: React.ReactNode;
}

const TableHeader: React.FC<TableHeaderProps> = ({ title, description, itemCount, onSearch, buttons }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    onSearch(query);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-5 pl-7 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg">
      <div className="flex flex-col items-start mb-4 sm:mb-0">
        <div className="flex items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mr-2">{title}</h3>
          <span className="px-2 py-0.5 text-[12px] font-medium text-blue-800 bg-blue-100 rounded-full dark:bg-blue-900 dark:text-blue-200 animate-fade-in">
            {itemCount} items
          </span>
        </div>
        {description && (
          <p className="py-1 text-sm text-gray-500 max-w-2xl">
            {description}
          </p>
        )}
      </div>
      <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
        <div className="relative w-full sm:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-300 ease-in-out"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <div className="flex space-x-2">
          {buttons}
        </div>
      </div>
    </div>
  );
};

interface TableComponentProps<TData extends object> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  title: string;
  description?: string; // New prop for description
  buttons: React.ReactNode;
  onRowClick?: (row: TData) => void;
  isLoading?: boolean;
  loadingContent?: React.ReactNode;
  isError?: boolean; // New prop for error state
  errorContent?: React.ReactNode; // New prop for custom error content
  paginationType?: 'lazy' | 'traditional'; // New prop for pagination variant
  hasMore?: boolean;
  onLoadMore?: () => void;
}

const TableComponent = <TData extends object>({
  data,
  columns,
  title,
  description, // Destructure new prop
  buttons,
  onRowClick,
  isLoading,
  loadingContent,
  isError, // Destructure new prop
  errorContent, // Destructure new prop
  paginationType = 'traditional', // Default to traditional pagination
  hasMore = false,
  onLoadMore,
}: TableComponentProps<TData>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(paginationType === 'traditional' && { // Conditionally add pagination model
      getPaginationRowModel: getPaginationRowModel(),
    }),
    debugTable: false,
  });

  // Intersection Observer for lazy loading
  React.useEffect(() => {
    if (paginationType !== 'lazy' || !onLoadMore || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoading && hasMore) {
          onLoadMore();
        }
      },
      { root: tableContainerRef.current, rootMargin: '0px 0px 100px 0px' } // Trigger 100px before end
    );

    const lastRow = tableContainerRef.current?.querySelector('tbody tr:last-child');
    if (lastRow) {
      observer.observe(lastRow);
    }

    return () => {
      if (lastRow) {
        observer.unobserve(lastRow);
      }
    };
  }, [tableContainerRef, onLoadMore, isLoading, hasMore, paginationType, table.getRowModel().rows.length]);


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden ring-1 ring-black ring-opacity-5 transition-all duration-300 ease-in-out dark:border dark:border-gray-700 flex flex-col h-full" // Added flex flex-col h-full
    >
      <TableHeader
        title={title}
        description={description} // Pass description prop
        itemCount={table.getFilteredRowModel().rows.length}
        onSearch={setGlobalFilter}
        buttons={buttons}
      />
      <div ref={tableContainerRef} className="overflow-x-auto flex-grow relative"> {/* Changed max-h to flex-grow */}
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50/50 dark:bg-gray-700/50 sticky top-0 z-10">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    scope="col"
                    className={`px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider select-none ${
                      header.column.getCanSort() ? 'cursor-pointer' : ''
                    }`}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center group">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <AnimatePresence mode="wait">
                            {header.column.getIsSorted() === 'asc' && (
                              <motion.span
                                key="asc"
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                className="ml-2 transition-transform duration-200 ease-out"
                              >
                                <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              </motion.span>
                            )}
                            {header.column.getIsSorted() === 'desc' && (
                              <motion.span
                                key="desc"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                className="ml-2 transition-transform duration-200 ease-out"
                              >
                                <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              </motion.span>
                            )}
                            {!header.column.getIsSorted() && (
                              <motion.span
                                key="none"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="ml-2 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-out"
                              >
                                <ChevronsUpDown className="h-4 w-4" />
                              </motion.span>
                            )}
                          </AnimatePresence>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr >
            ))}
          </thead >
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {isError ? (
              <tr className="h-24">
                <td colSpan={columns.length} className="text-center text-gray-500 dark:text-gray-400">
                  {errorContent ? (
                    <div className="flex items-center justify-center py-4">
                      {errorContent}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-4 text-red-500">
                      <AlertCircle className="h-6 w-6 mr-3" />
                      <span>Failed to load data.</span>
                    </div>
                  )}
                </td>
              </tr>
            ) : isLoading && table.getRowModel().rows.length === 0 ? (
              <tr className="h-24">
                <td colSpan={columns.length} className="text-center text-gray-500 dark:text-gray-400">
                  {loadingContent ? (
                    <div className="flex items-center justify-center py-4">
                      {loadingContent}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                      <span className="ml-3">Loading data...</span>
                    </div>
                  )}
                </td>
              </tr>
            ) : table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <motion.tr
                  key={row.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: row.index * 0.05 }}
                  className={`transition-colors duration-150 ease-in-out ${onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : ''}`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </motion.tr>
              ))
            ) : (
              <tr className="h-24">
                <td colSpan={columns.length} className="text-center text-gray-500 dark:text-gray-400">
                  No data available.
                </td>
              </tr>
            )}
            {(isLoading && hasMore && paginationType === 'lazy') && ( // Show loading indicator at the bottom for lazy loading
              <tr className="h-24">
                <td colSpan={columns.length} className="text-center text-gray-500 dark:text-gray-400">
                  {loadingContent ? (
                    <div className="flex items-center justify-center py-4">
                      {loadingContent}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                      <span className="ml-3">Loading more data...</span>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table >
      </div >
      {paginationType === 'traditional' && ( // Conditionally render pagination controls
        <div className="flex items-center justify-end p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg space-x-1">
          <span className="text-sm text-gray-700 dark:text-gray-300 pr-2">
            Page{' '}
              {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="p-1 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="p-1 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default TableComponent;
